import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Order } from '../../services/exports/Interfaces';
import { DeliveryProvider, ORDER_FULFILLMENT_STATUSES, ORDER_METHODS } from '../../services/exports/Constants';
import useAppMethods from '../../hooks/utility/useAppMethods';
import LoadingIndicator from '../loading/LoadingIndicator';
import DeliveryCarrierIcon from './DeliveryCarrierIcon';
import { Stringable } from '../../services/helpers/Stringable';
import classnames from 'classnames';
import useScreenType from '../../hooks/utility/useScreenType';

interface Props {
  order: Order;
  className?: string;
}

export default function DeliveryTracker({ order, className }: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Delivery:DeliveryTracker' });

  return (
    order.method === ORDER_METHODS.DELIVERY &&
    order.delivery_provider !== DeliveryProvider.Standalone &&
    order.fulfillment.status !== ORDER_FULFILLMENT_STATUSES.REJECTED && (
      <button
        onClick={() => window.open(order.delivery?.tracking_url, '_blank')}
        disabled={!order.delivery?.tracking_url}
        className={classnames(
          'mt-lg flex justify-center items-center py-sm px-2xl text-brand-primary text-center rounded-md border font-medium disabled:bg-white',
          className,
        )}
      >
        {!order.delivery?.tracking_url ? <LoadingIndicator lg={24} md={24} sm={24} className="mr-xs my-auto" /> : null}
        {t(order.delivery?.tracking_url ? 'buttons.track_order' : 'buttons.track_order_once_confirmed')}
      </button>
    )
  );
}

DeliveryTracker.DeliveredBy = ({ order, className }: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Delivery:DeliveryTracker' });
  const carrier = order?.delivery?.carrier ?? order?.delivery_quote?.carrier;

  return (
    order.method === ORDER_METHODS.DELIVERY &&
    order.delivery_provider !== DeliveryProvider.Standalone &&
    order.fulfillment.status !== ORDER_FULFILLMENT_STATUSES.REJECTED && (
      <div className={classnames('flex', className)}>
        <p className="my-auto mr-xs">{t('labels.delivered_by')}</p>
        <DeliveryCarrierIcon carrier={carrier} className="h-4 w-initial my-auto" />
      </div>
    )
  );
};

DeliveryTracker.DeliveryContent = ({ order, className }: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Delivery:DeliveryTracker' });
  const { isDesktop } = useScreenType();
  const { formatCurrency } = useAppMethods();
  const carrier = order?.delivery?.carrier ?? order?.delivery_quote?.carrier;

  return (
    order.method === ORDER_METHODS.DELIVERY &&
    order.delivery_provider !== DeliveryProvider.Standalone &&
    order.fulfillment.status !== ORDER_FULFILLMENT_STATUSES.REJECTED && (
      <div className={classnames('space-y-md', className)}>
        <div className="rounded-lg">
          <p className="text-sm">
            <Trans
              i18nKey={t('text', {
                carrier: new Stringable(carrier).capitalize().toString(),
                savings: `5-${formatCurrency(10)}`,
              })}
              components={[<b key="0" className="font-semibold" />, <b key="1" className="font-semibold" />]}
            />
          </p>
        </div>
        {isDesktop && <DeliveryTracker.DeliveredBy order={order} />}
      </div>
    )
  );
};
